import * as React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { acceptInvitationInputSchema } from '@lib/input/user.input'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { AuthShell } from '@/components/AuthShell'
import { AccountEmailField } from '@/components/AccountEmailField'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FormError } from '@/components/ui/form-error'

/** Public accept-invitation page reached from the emailed link (`?token=`).
 * Sets the first password, then signs the user straight in. */
export function AcceptInvitePage() {
  const { acceptInvitation } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [token] = React.useState(() => params.get('token') ?? '')
  const [password, setPassword] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // The token sets the first password. It is already in state, so it does not
  // stay in the address bar, where history and the next request's Referer would keep it.
  React.useEffect(() => {
    if (window.location.search.length > 0) {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    if (password !== confirm) {
      setError('Passwords don’t match.')
      return
    }
    const parsed = acceptInvitationInputSchema.safeParse({ token, password })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid password.')
      return
    }
    setSubmitting(true)
    try {
      const redirectTo = await acceptInvitation(parsed.data)
      navigate(redirectTo ?? '/', { replace: true })
    } catch (err) {
      setError(isApiError(err) ? err.body.message ?? 'Could not accept the invitation.' : toErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-display">Set your password</CardTitle>
          <CardDescription>Choose a password to activate your account.</CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted">
                This invitation link is invalid or incomplete. Ask your administrator to resend the invite.
              </p>
              <Link to="/login" className="text-sm underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <AccountEmailField token={token} />
              <Field label="Password" htmlFor="password">
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Field label="Confirm password" htmlFor="confirm">
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </Field>
              {error ? <FormError>{error}</FormError> : null}
              <Button pending={submitting} type="submit" className="w-full">
                {submitting ? 'Activating…' : 'Activate account'}
              </Button>
              {/* The sign-in page links these; an invited couple activates here
                  and never sees that page. Links only: no "you agree" line while
                  the documents are drafts. */}
              <p className="text-sm text-ink-muted">
                <Link to="/terms" className="underline">
                  Terms of service
                </Link>
                {' · '}
                <Link to="/privacy" className="underline">
                  Privacy policy
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  )
}
